import { FileCheck2, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { Select } from '@/components/ui/select'
import { fmtMoney } from '../../shared/format'
import { useCreateEDoc } from '../api'
import { EDOC_TYPES, type EDocType, type Transaction } from '../types'
import { toastError, useFinanceLookups } from '../ui'
import { Field, FormSection } from './FormBits'

type Props = { transaction: Transaction | null; candidates?: Transaction[]; onClose: () => void }

/** Gelir kaydından taslak e-Fatura / e-Arşiv oluşturur; gönderim GİB e-Belgeler ekranından yapılır. */
export function EDocCreateModal(props: Props) {
  const open = props.transaction !== null || !!props.candidates
  return open ? <EDocForm key={props.transaction?.id ?? 'pick'} {...props} /> : null
}

function EDocForm({ transaction, candidates, onClose }: Props) {
  const { t } = useTranslation()
  const create = useCreateEDoc()
  const { partyName } = useFinanceLookups()
  const [type, setType] = useState<EDocType>('e_invoice')
  const [txId, setTxId] = useState(transaction?.id ?? '')
  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!txId) return
    try {
      await create.mutateAsync({ type, transactionId: txId })
      toast.success(t('finance.edoc.created'))
      onClose()
    } catch (err) {
      toastError(err)
    }
  }

  const list = transaction ? [transaction] : (candidates ?? [])
  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      dismissible={!create.isPending}
      icon={<FileCheck2 />}
      title={t('finance.edoc.createTitle')}
      description={t('finance.edoc.createHint')}
      onSubmit={(e) => void submit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={create.isPending || !txId}>
            {create.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('finance.edoc.create')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <FormSection>
          <Field id="ed-tx" label={t('finance.edoc.sourceTx')} full>
            <Select
              id="ed-tx"
              value={txId}
              onChange={setTxId}
              disabled={!!transaction}
              placeholder={t('common.select')}
              options={list.map((x) => ({ value: x.id, label: `${partyName(x.counterpartyId)} · ${x.docNo || x.description}`, description: fmtMoney(x.total), keywords: [x.docNo, x.description] }))}
            />
          </Field>
        </FormSection>
        <fieldset className="space-y-2">
          <legend className="mb-1 text-sm font-medium">{t('finance.edoc.type')}</legend>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {EDOC_TYPES.map((k) => (
              <label key={k} className="flex cursor-pointer items-start gap-2 rounded-lg border p-3 has-[:checked]:border-primary/50 has-[:checked]:bg-primary/[0.05]">
                <input type="radio" name="edoc-type" className="mt-0.5 accent-[var(--color-primary)]" checked={type === k} onChange={() => setType(k)} />
                <span>
                  <span className="block text-sm font-medium">{t(`finance.edoc.types.${k}`)}</span>
                  <span className="block text-xs text-muted-foreground">{t(`finance.edoc.typeHint.${k}`)}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
    </Modal>
  )
}
